import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  BookOpen, 
  Target, 
  TrendingUp, 
  Info,
  Users,
  Clock,
  BarChart3,
  Compass,
  ArrowRight,
  HelpCircle,
  Star,
  CheckCircle
} from 'lucide-react';
import { StartHereGuidance } from './StartHereGuidance';
import { useUserProgress } from '@/hooks/useUserProgress';
import { useAuth } from '@/hooks/useAuth';

interface DisciplineEntryPanelProps {
  disciplineId: string;
  disciplineName: string;
  disciplineDescription?: string;
  totalNodes: number;
  userCompletedNodes?: number;
  onStartRecommendation?: (nodeId: string) => void;
}

export const DisciplineEntryPanel: React.FC<DisciplineEntryPanelProps> = ({
  disciplineId,
  disciplineName,
  disciplineDescription,
  totalNodes,
  userCompletedNodes = 0,
  onStartRecommendation
}) => {
  const [activeTab, setActiveTab] = useState('start-here');
  const { user } = useAuth();
  const { data: userProgress } = useUserProgress();

  // Calculate completion rate
  const completionRate = totalNodes > 0 ? Math.round((userCompletedNodes / totalNodes) * 100) : 0;
  
  // Determine user's status in this discipline
  const getUserStatus = () => {
    if (userCompletedNodes === 0) return 'newcomer';
    if (completionRate < 25) return 'beginner';
    if (completionRate < 50) return 'intermediate';
    if (completionRate < 75) return 'advanced';
    if (completionRate < 100) return 'expert';
    return 'completed';
  };

  const userStatus = getUserStatus();

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'newcomer':
        return {
          icon: Compass,
          color: 'text-black bg-white',
          title: 'Welcome to the Journey',
          description: 'Start your exploration of this fascinating discipline',
          action: 'Begin Learning'
        };
      case 'beginner':
        return {
          icon: BookOpen,
          color: 'text-green-600 bg-white',
          title: 'Building Foundations',
          description: 'Continue building your understanding with guided recommendations',
          action: 'Continue Learning'
        };
      case 'intermediate':
        return {
          icon: TrendingUp,
          color: 'text-black bg-white',
          title: 'Progressing Well',
          description: 'Dive deeper with intermediate and advanced content',
          action: 'Explore Further'
        };
      case 'advanced':
        return {
          icon: Target,
          color: 'text-black bg-white',
          title: 'Advanced Learner',
          description: 'Challenge yourself with research-level content',
          action: 'Take on Challenges'
        };
      case 'expert':
        return {
          icon: Star,
          color: 'text-black bg-white',
          title: 'Near Mastery',
          description: 'Complete your mastery of this discipline',
          action: 'Finish Strong'
        };
      case 'completed':
        return {
          icon: CheckCircle,
          color: 'text-green-600 bg-white',
          title: 'Discipline Mastered',
          description: 'Congratulations! Consider exploring related disciplines',
          action: 'Explore Related'
        };
      default:
        return {
          icon: BookOpen,
          color: 'text-black bg-white',
          title: 'Ready to Learn',
          description: 'Begin your learning journey in this discipline',
          action: 'Start Learning'
        };
    }
  };

  const statusConfig = getStatusConfig(userStatus);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${statusConfig.color}`}>
                <statusConfig.icon className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl">{statusConfig.title}</CardTitle>
                <CardDescription className="text-base mt-1">
                  {statusConfig.description}
                </CardDescription>
                {disciplineDescription && (
                  <p className="text-sm text-black mt-2">
                    {disciplineDescription}
                  </p>
                )}
              </div>
            </div>
            
            {/* Progress Summary */}
            <div className="text-right">
              <div className="text-2xl font-bold">{completionRate}%</div>
              <div className="text-sm text-black">Complete</div>
              <div className="text-xs text-black mt-1">
                {userCompletedNodes} of {totalNodes} nodes
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Navigation Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="start-here" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Start Here
          </TabsTrigger>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="help" className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            Help & Tips
          </TabsTrigger>
        </TabsList>

        {/* Start Here Tab */}
        <TabsContent value="start-here" className="space-y-4">
          {user ? (
            <StartHereGuidance 
              disciplineId={disciplineId}
              disciplineName={disciplineName}
              onRecommendationSelect={onStartRecommendation}
              showUserProfile={true}
              compact={false}
            />
          ) : (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Sign in to get personalized recommendations based on your learning style and preferences.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Learning Path Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Learning Structure
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-black">{totalNodes}</div>
                    <div className="text-sm text-black">Total Content</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {Math.round(totalNodes * 0.2)} {/* Estimate of entry points */}
                    </div>
                    <div className="text-sm text-black">Entry Points</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Foundational (Level 1-2)</span>
                    <span>{Math.round(totalNodes * 0.4)} nodes</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Intermediate (Level 3)</span>
                    <span>{Math.round(totalNodes * 0.35)} nodes</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Advanced (Level 4-5)</span>
                    <span>{Math.round(totalNodes * 0.25)} nodes</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Progress Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Your Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {user ? (
                  <>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-black">{completionRate}%</div>
                      <div className="text-sm text-black">Completion Rate</div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Completed</span>
                        <span className="font-medium text-green-600">{userCompletedNodes}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Remaining</span>
                        <span className="font-medium">{totalNodes - userCompletedNodes}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Status</span>
                        <Badge variant="outline" className={statusConfig.color}>
                          {statusConfig.title}
                        </Badge>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <Users className="h-8 w-8 mx-auto text-black mb-2" />
                    <p className="text-sm text-black">
                      Sign in to track your progress and see personalized insights
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Help & Tips Tab */}
        <TabsContent value="help" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Learning Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  Learning Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">For New Learners:</h4>
                  <ul className="text-sm text-black space-y-1 ml-4">
                    <li>• Start with foundational content (Level 1-2)</li>
                    <li>• Follow the recommended learning paths</li>
                    <li>• Take notes and review regularly</li>
                    <li>• Don't rush - understanding is key</li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">For Advanced Learners:</h4>
                  <ul className="text-sm text-black space-y-1 ml-4">
                    <li>• Challenge yourself with higher levels</li>
                    <li>• Explore cross-disciplinary connections</li>
                    <li>• Engage with research-level content</li>
                    <li>• Consider contributing insights</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Navigation Help */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Compass className="h-5 w-5" />
                  Navigation Guide
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Getting Started:</h4>
                  <ul className="text-sm text-black space-y-1 ml-4">
                    <li>• Use "Start Here" for personalized recommendations</li>
                    <li>• Check your progress in the Overview tab</li>
                    <li>• Browse all content in the main discipline view</li>
                    <li>• Use search to find specific topics</li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Understanding Recommendations:</h4>
                  <ul className="text-sm text-black space-y-1 ml-4">
                    <li>• Match percentage shows content alignment</li>
                    <li>• Difficulty levels guide progression</li>
                    <li>• Time estimates help plan study sessions</li>
                    <li>• Rationales explain why content is suggested</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" size="sm" onClick={() => setActiveTab('start-here')}>
                  <Target className="h-4 w-4 mr-2" />
                  Get Recommendations
                </Button>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('overview')}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Progress
                </Button>
                <Button variant="outline" size="sm">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Browse All Content
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}; 